/**
 * Normalize a locale or language code to lowercase hyphen-separated form.
 * Examples:
 * - EN -> en
 * - en_US -> en-us
 * - en-US -> en-us
 */
export const normalizeLocaleCode = (value?: string | null): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.trim().replace(/_/g, '-').replace(/-+/g, '-').toLowerCase();

    return normalized.length > 0 ? normalized : null;
};

/**
 * Return the base language for a locale-like string.
 * Examples:
 * - en-gb -> en
 * - en -> en
 */
export const getBaseLanguage = (value?: string | null): string | null => {
    const normalized = normalizeLocaleCode(value);
    if (!normalized) {
        return null;
    }

    const [baseLanguage] = normalized.split('-');
    return baseLanguage || null;
};

/**
 * Accept simple language codes plus hyphenated locale variants.
 * Examples:
 * - en
 * - en-gb
 * - zh-hant
 */
export const isLocaleCode = (value?: string | null): boolean => {
    const normalized = normalizeLocaleCode(value);
    if (!normalized) {
        return false;
    }

    return /^[a-z]{2}(?:-[a-z0-9]{2,8})*$/.test(normalized);
};

/**
 * Build exact-then-base lookup candidates for locale-aware maps.
 * Example:
 * - en-gb -> ['en-gb', 'en']
 * - en -> ['en']
 */
export const getLocaleLookupCandidates = (value?: string | null): string[] => {
    const normalized = normalizeLocaleCode(value);
    if (!normalized) {
        return [];
    }

    const baseLanguage = getBaseLanguage(normalized);
    if (!baseLanguage || baseLanguage === normalized) {
        return [normalized];
    }

    return [normalized, baseLanguage];
};

/**
 * Normalize a record keyed by language or locale codes.
 */
export const normalizeLocaleRecord = <T>(record?: Record<string, T> | null): Record<string, T> => {
    if (!record) {
        return {};
    }

    return Object.entries(record).reduce(
        (acc, [key, value]) => {
            const normalizedKey = normalizeLocaleCode(key) ?? key.trim().toLowerCase();
            acc[normalizedKey] = value;
            return acc;
        },
        {} as Record<string, T>
    );
};

/**
 * Resolve an exact locale first, then fall back to its base language.
 * Example:
 * - locale en-gb with { en-gb: 'UK', en: 'Generic English' } -> 'UK'
 * - locale en-gb with { en: 'Generic English' } -> 'Generic English'
 */
export const findBestLocaleValue = <T>(
    record: Record<string, T> | null | undefined,
    locale?: string | null
): T | undefined => {
    const normalizedRecord = normalizeLocaleRecord(record);

    for (const candidate of getLocaleLookupCandidates(locale)) {
        if (Object.prototype.hasOwnProperty.call(normalizedRecord, candidate)) {
            return normalizedRecord[candidate];
        }
    }

    return undefined;
};
